import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import {
    FunnelDashboardStats,
    FIYRPOD_DARK,
    FIYRPOD_GREEN,
    FIYRPOD_MUTED,
} from '../../salesFunnel.types';

interface FunnelSummaryCardsProps {
    stats: FunnelDashboardStats;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const FunnelSummaryCards: React.FC<FunnelSummaryCardsProps> = ({ stats }) => {
    const monthDelta = stats.prospects_this_month - stats.prospects_last_month;
    const TrendIcon =
        monthDelta > 0 ? TrendingUpIcon : monthDelta < 0 ? TrendingDownIcon : TrendingFlatIcon;
    const trendColor = monthDelta > 0 ? FIYRPOD_GREEN : monthDelta < 0 ? '#ef5350' : FIYRPOD_MUTED;

    const cards = [
        {
            label: 'Total Prospects',
            value: stats.total_prospects.toString(),
            subtitle: `${stats.prospects_this_month} added this month`,
            icon: PeopleAltIcon,
            showTrend: true,
        },
        {
            label: 'Pipeline Value',
            value: formatCurrency(stats.total_pipeline_value),
            subtitle: 'Estimated total deal value',
            icon: AttachMoneyIcon,
            showTrend: false,
        },
        {
            label: 'Conversion Rate',
            value: `${stats.conversion_rate}%`,
            subtitle: 'Identified → Closed',
            icon: CheckCircleIcon,
            showTrend: false,
        },
        {
            label: 'Avg Days to Close',
            value: stats.avg_days_in_funnel ? `${stats.avg_days_in_funnel}d` : '--',
            subtitle: 'From identified to signed',
            icon: TimerIcon,
            showTrend: false,
        },
    ];

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {cards.map((card) => {
                const Icon = card.icon;
                return (
                    <Grid item xs={12} sm={6} md={3} key={card.label}>
                        <Paper
                            elevation={0}
                            sx={{
                                background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                                border: '1px solid #1e3050',
                                borderRadius: 2,
                                p: 2.5,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    background: FIYRPOD_GREEN,
                                },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: FIYRPOD_MUTED,
                                        fontWeight: 600,
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        fontSize: '0.65rem',
                                    }}
                                >
                                    {card.label}
                                </Typography>
                                <Icon sx={{ fontSize: 18, color: FIYRPOD_GREEN, opacity: 0.7 }} />
                            </Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 800,
                                    my: 0.75,
                                    color: FIYRPOD_GREEN,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {card.value}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {card.showTrend && (
                                    <TrendIcon sx={{ fontSize: 14, color: trendColor }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{ color: FIYRPOD_MUTED, fontSize: '0.7rem' }}
                                >
                                    {card.subtitle}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default FunnelSummaryCards;
